import React, { MouseEvent, useCallback } from 'react'
import { useSearchBox } from 'react-instantsearch'
import styled, { useTheme } from 'styled-components'
import { useMediaQuery } from '@material-ui/core'
import { irem } from '../../utils/rem'
import { useRouter } from 'next/router'

export type AutocompleteResultsProps = {
  setInput: (string) => void
  hits: any[]
}

const StyledSuggestionList = styled.ul`
  list-style: none;
  position: absolute;
  top: calc(100% - ${irem(16)});
  background: white;
  left: ${props => props.theme.typography.pxToRem(16)};
  right: ${props => props.theme.typography.pxToRem(16)};
  border-top: 2px solid ${props => props.theme.palette.primary.main};
  padding: ${props => props.theme.spacing(1)} 0;
  margin: 0;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);

  ${props => props.theme.breakpoints.up('sm')} {
    left: 0;
    right: 0;
    top: calc(100% - 2px);
  }
`

const StyledSuggestionListItem = styled.li`
  margin: 0;
  padding: 0;
  width: 100%;
  max-width: 100%;

  > a {
    display: block;
    padding: ${props => props.theme.spacing(1)} ${props => props.theme.spacing(3)};
    color: inherit;
    text-decoration: none;

    &:hover,
    &:focus,
    &:active {
      background: #f2f2f2;
    }
  }

  &::before {
    display: none;
  }
`
const getDisplayTitle = (hit: any) => {
  if (hit.objectType === 'pdf' && hit.fileName) {
    // For PDF documents, check if title is corrupted
    const hasCorruptedTitle = !hit.title || hit.title.includes('�') || hit.title.trim().length === 0

    if (hasCorruptedTitle) {
      // Use filename without extension as fallback
      return hit.fileName.replace(/\.(pdf|doc|docx)$/i, '').replace(/[_-]/g, ' ')
    }

    return hit.title
  }

  // For regular pages, use existing logic
  return (hit.headers && hit.headers[0] === 'Frequently Asked Questions' && hit.headers[1])
    ? hit.headers[1]
    : (hit.headers && hit.headers[0]) || hit.title
}

export const AutocompleteResults: React.VFC<AutocompleteResultsProps> = ({ setInput, hits }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { refine } = useSearchBox()
  const router = useRouter()

  const onClick = useCallback((e: MouseEvent<HTMLAnchorElement>, title: string) => {
    setInput(title)
    refine(title)

    if (router?.route === '/search') {
      e.preventDefault()
    }
  }, [router, setInput, refine])

  // Filter and limit results for autocomplete
  const autocompleteHits = hits && Array.isArray(hits)
    ? hits.filter(hit => {
      const displayTitle = getDisplayTitle(hit)

      return displayTitle && displayTitle.trim().length > 0
    }).slice(0, isMobile ? 3 : 6)
    : []

  return autocompleteHits.length > 0 ? (
    <StyledSuggestionList>
      {autocompleteHits.map((hit) => {
        const displayTitle = getDisplayTitle(hit)

        return (
          <StyledSuggestionListItem key={hit.objectID}>
            <a href={`/search?q=${encodeURIComponent(displayTitle)}`} onClick={(e) => onClick(e, displayTitle)}>
              {displayTitle}
            </a>
          </StyledSuggestionListItem>
        )
      })}
    </StyledSuggestionList>
  ) : null
}